import { initAxios } from '../../../utils/axiosHelper';
import { parseErrorMsg } from '../../../utils/errorHelper';
import EntityServiceAPI from '../../EntityServiceAPI';
import ProductServiceAPI from '../../ProductServiceAPI';
import {
  IEntityScreening,
  IEntityScreeningHistoryResponse,
  IProductScreeningHistoryResponse,
  IRunEntityScreeningRequest,
  IRunProductCheckRequest,
  IRunProductScreeningRequest,
  IRunScreeningRequest,
  IRunSelectEntitiesRequest,
  IRunTariffCheckRequest,
  IScreeningResultRow,
} from './screening.d';

export const getScreeningResultsByDatabaseId = async(
  databaseId: string,
): Promise<{ success: boolean; result: IScreeningResultRow[] }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: [] as IScreeningResultRow[],
      };
    }

    const response = await apiClient.get(
      `bite-db/screening-history/database/${databaseId}`,
    );

    return {
      success: true,
      result: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};


export const getScreeningResultsForGroup = async(
  groupId: string,
): Promise<{ success: boolean; result: IScreeningResultRow[] }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: [] as IScreeningResultRow[],
      };
    }

    const response = await apiClient.get(
      `bite-db/screening-history-group/${groupId}`,
    );

    return {
      success: true,
      result: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      result: [] as IScreeningResultRow[],
    };
  }
};

export const fetchEntityScreening = async(
  screeningData: IEntityScreening,
): Promise<{ success: boolean; result: any }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    const response = await apiClient.post(
      '/bite-db/screen-histories',
      JSON.stringify(screeningData),
    );

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const fetchEntityScreeningHistory = async(params: {
  database_id: string;
  group_id: string;
  entity_name: string;
}): Promise<{ success: boolean; data: any }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        data: 'Error initializing api client',
      };
    }

    const screeningData = {
      id: params.database_id,
      group_Id: params.group_id,
      value: params.entity_name,
    };

    const response = await apiClient.post(
      '/bite-db/screen-histories',
      JSON.stringify(screeningData),
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      data: parseErrorMsg(error),
    };
  }
};


export const runDatabaseScreening = async(
  screeningRequest: IRunScreeningRequest,
): Promise<{ success: boolean; result: any }> => {
  try {
    const apiClient = await initAxios('/automations');
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    const response = await apiClient.post('screenings/run', screeningRequest);

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const runEntityServiceDatabaseScreening = async(params: {
  databaseId: string;
  screenedBy?: string;
}): Promise<{ success: boolean; result: any }> =>
  // Delegates to the shared entity-service wrapper, which posts to
  // `POST screening/run` through the same-origin nginx proxy.
  EntityServiceAPI.runDatabaseScreening({
    databaseId: params.databaseId,
    screenedBy: params.screenedBy,
  });


export const runRetryScreeningForEntities = async(
  request: IRunSelectEntitiesRequest,
): Promise<{ success: boolean; result: any }> =>
  // Scoped entity re-screen through the entity service (replaces the automations
  // `screenings/run/select-entities` endpoint). `entityIds` are DatabaseEntity ids.
  EntityServiceAPI.runEntityScreening({
    databaseId: request.databaseId,
    databaseEntityIds: request.entityIds,
  });


export const runEntityScreening = async(
  screeningRequest: IRunEntityScreeningRequest | IRunEntityScreeningRequest[],
): Promise<{ success: boolean; result: any }> => {
  // Scoped entity re-screen through the entity service (replaces the automations
  // `screenings/run/entity` endpoint). Group the requested entities by database
  // and publish each database's scoped entity ids to the screening pipeline.
  const requests = Array.isArray(screeningRequest)
    ? screeningRequest
    : [ screeningRequest ];

  const byDatabase = new Map<string, Array<string | number>>();
  for (const request of requests) {
    if (request.databaseId === undefined || request.databaseId === null) continue;
    const entityId = request.databaseEntityId ?? request.entityId;
    if (entityId === undefined || entityId === null) continue;
    const databaseId = String(request.databaseId);
    const ids = byDatabase.get(databaseId) ?? [];
    ids.push(entityId);
    byDatabase.set(databaseId, ids);
  }

  if (byDatabase.size === 0) {
    return { success: false, result: 'No database entity ids provided' };
  }

  const results = await Promise.all(
    Array.from(byDatabase.entries()).map(([ databaseId, databaseEntityIds ]) =>
      EntityServiceAPI.runEntityScreening({ databaseId: databaseId, databaseEntityIds: databaseEntityIds }),
    ),
  );

  return results.length === 1
    ? results[0]
    : { success: results.every((r) => r.success), result: results };
};

export const runTariffCheck = async(
  checkRequest: IRunTariffCheckRequest,
): Promise<{ success: boolean; result: any }> => {
  try {
    // Tariff checks run on the biteservices analyzer service (automations'
    // tariff-check route was retired). Same base-URL rule as tariffs.service.ts:
    // QA/prod set REACT_APP_TARIFF_API_URL, locally the nginx `/analyzer-service`
    // proxy. The fallback must stay non-empty or initAxios hits bite-server.
    const apiClient = await initAxios(
      // eslint-disable-next-line no-undef
      process.env.REACT_APP_TARIFF_API_URL || '/analyzer-service',
    );
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    // ✅ POST to the tariff-specific run endpoint
    const response = await apiClient.post('tariff-check/run', checkRequest);

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const runProductCheck = async(
  checkRequest: IRunProductCheckRequest,
): Promise<{ success: boolean; result: any }> => {
  try {
    // Uses the same automations client as entity screening
    const apiClient = await initAxios('/automations');
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    // ✅ POST to the product-specific run endpoint.
    // This is the explicit "Run Screening" action, so recompute tariffs as-of the
    // screen date (tariffDateMode: 'screen') unless the caller overrides it.
    const response = await apiClient.post('screenings/run/products', {
      tariffDateMode: 'screen',
      ...checkRequest,
    });

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

// On-demand CROSS ruling lookup for a single product. CROSS rulings used to be
// fetched for every product during bulk screening (an external AI call each); they
// are now fetched here only when a user opens a product. The product service
// reads hts_code/description from the DatabaseProduct row itself (so those params
// are no longer sent) and caches the result to DatabaseProduct.cross_rulings.
export const fetchProductCrossRulings = async (params: {
  databaseProductId?: string;
  hts_code?: string;
  product_description?: string;
}): Promise<string[]> => {
  if (!params.databaseProductId) {
    return [];
  }
  const response = await ProductServiceAPI.getProductCrossRulings(
    params.databaseProductId,
  );
  return response.success && Array.isArray(response.result?.cross_data_links)
    ? response.result.cross_data_links
    : [];
};

export const runProductCheckSingle = async(
  checkRequest: IRunProductScreeningRequest,
): Promise<{ success: boolean; result: any }> =>
  ProductServiceAPI.runProductCheckSingle(checkRequest);

export const getDatabaseScreeningStatus = async(
  databaseId: string,
  useEntityService = false,
): Promise<{ success: boolean; result: any }> => {
  // Entity databases are screened by the entity service, which exposes live
  // progress at GET /screening/status through the same-origin nginx proxy;
  // product and tariff databases report through the product service's
  // GET /screenings/status (the analyzer's tariff check writes the same
  // ScreeningResult rows it reads).
  if (useEntityService) {
    return EntityServiceAPI.getDatabaseScreeningStatus(databaseId);
  }

  return ProductServiceAPI.getDatabaseScreeningStatus(databaseId);
};

export const toggleEntityDocVisibility = async(
  parentId: string,
  itemId: string,
  reason?: string,
  itemName?: string,
): Promise<{ success: boolean; result: any }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    const reqBody = {
      parent_id: parentId,
      item_id: itemId,
      ...(reason ? { reason: reason } : {}),
      ...(itemName ? { item_name: itemName } : {}),
    };

    const response = await apiClient.post(
      '/bite-db/toggle-entity-doc-visibility',
      reqBody,
    );

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export interface IBulkHideEntityHitsRequest {
  entities: Array<{
    database_entity_id: string;
    hit_ids: string[];
  }>;
  comment?: string;
}

export interface IBulkHideEntityHitsResponse {
  action_id: string;
  requested_hits: number;
  hidden_hits: number;
  already_hidden_hits: number;
  entities: Array<{
    database_entity_id: string;
    hidden_hit_ids: string[];
    already_hidden_hit_ids: string[];
  }>;
}

export interface IBulkUnhideEntityHitsResponse {
  action_id: string;
  requested_hits: number;
  unhidden_hits: number;
  already_visible_hits: number;
  entities: Array<{
    database_entity_id: string;
    unhidden_hit_ids: string[];
    already_visible_hit_ids: string[];
  }>;
}

export const bulkHideEntityHits = async(
  payload: IBulkHideEntityHitsRequest,
  selectedTeamId: string,
): Promise<IBulkHideEntityHitsResponse> => {
  const apiClient = await initAxios();
  if (!apiClient) {
    throw new Error('Error initializing API client');
  }

  const response = await apiClient.post(
    '/bite-db/entity-hits/bulk-hide',
    payload,
    {
      headers: {
        'x-selected-team-id': selectedTeamId,
      },
    },
  );

  return response.data.data;
};

export const bulkUnhideEntityHits = async(
  payload: IBulkHideEntityHitsRequest,
  selectedTeamId: string,
): Promise<IBulkUnhideEntityHitsResponse> => {
  const apiClient = await initAxios();
  if (!apiClient) {
    throw new Error('Error initializing API client');
  }

  const response = await apiClient.post(
    '/bite-db/entity-hits/bulk-unhide',
    payload,
    {
      headers: {
        'x-selected-team-id': selectedTeamId,
      },
    },
  );

  return response.data.data;
};

/**
 * Toggle product document visibility
 * @param {string} parentId - The parent product ID
 * @param {string} itemId - The item ID to toggle visibility for
 * @returns {Promise<{success: boolean, result: any}>} Object containing success status and result data
 */
export const toggleProductDocVisibility = async(
  parentId: string,
  itemId: string,
  reason?: string,
  itemName?: string,
): Promise<{ success: boolean; result: any }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: 'Error initializing api client',
      };
    }

    const reqBody = {
      parent_id: parentId,
      item_id: itemId,
      ...(reason ? { reason: reason } : {}),
      ...(itemName ? { item_name: itemName } : {}),
    };

    const response = await apiClient.post(
      '/bite-db/toggle-product-doc-visibility',
      reqBody,
    );

    return {
      success: true,
      result: response.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const getScreeningRunsByDatabaseId = async(
  databaseId: string,
): Promise<{ success: boolean; result: any }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: { runs: [] },
      };
    }

    const response = await apiClient.get(
      `bite-db/screening-runs/database/${databaseId}`,
    );

    return {
      success: true,
      result: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const getScreeningHistoryByEntityId = async(
  databaseEntityId: string,
): Promise<{ success: boolean; result: IEntityScreeningHistoryResponse | null }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: null,
      };
    }

    const response = await apiClient.get(
      `bite-db/screening-history/entity/${databaseEntityId}`,
    );

    return {
      success: true,
      result: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};

export const getScreeningHistoryByProductId = async(
  databaseProductId: string,
): Promise<{ success: boolean; result: IProductScreeningHistoryResponse | null }> => {
  try {
    const apiClient = await initAxios();
    if (!apiClient) {
      return {
        success: false,
        result: null,
      };
    }

    const response = await apiClient.get(
      `bite-db/screening-history/product/${databaseProductId}`,
    );

    return {
      success: true,
      result: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      result: parseErrorMsg(error),
    };
  }
};
